import React, { useMemo, useRef, useEffect, useState } from "react";
import { Box } from "@mui/material";
import AutoLogo from "../../../../../static/Images/logo.png";
import getImageUrlByHash from "../../../../../static/logoUtils";

// High-end Casino harmonic palette for alternating segments
const SLICE_COLORS = [
  { fill: "#1e3a8a", text: "#ffffff", border: "#3b82f6" }, // Dark Royal Blue
  { fill: "#047857", text: "#ffffff", border: "#10b981" }, // Deep Emerald
  { fill: "#6d28d9", text: "#ffffff", border: "#8b5cf6" }, // Deep Purple
  { fill: "#b45309", text: "#ffffff", border: "#f59e0b" }, // Warm Amber
  { fill: "#b91c1c", text: "#ffffff", border: "#ef4444" }, // Crimson Red
  { fill: "#0e7490", text: "#ffffff", border: "#06b6d4" }, // Cyan
  { fill: "#4338ca", text: "#ffffff", border: "#6366f1" }, // Indigo
  { fill: "#a21caf", text: "#ffffff", border: "#d946ef" }, // Fuchsia
  { fill: "#c2410c", text: "#ffffff", border: "#f97316" }, // Orange
  { fill: "#0f766e", text: "#ffffff", border: "#14b8a6" }, // Teal
];

const ModernSpinWheel = ({
  capacity = 10,
  isSpinningContinuous = false,
  mustSpin = false,
  targetNumber = 1, // 1-indexed (1 to capacity)
  prizeNumber, // 0-indexed fallback
  selectedNumber = null,
  winningNumber = null,
  isWinner = false,
  onStopSpinning,
  partner,
  partnerLogo,
}) => {
  const numCapacity = Math.max(2, Number(capacity) || 10);
  const effectiveTarget = targetNumber || (prizeNumber !== undefined ? prizeNumber + 1 : 1);
  const [currentRotation, setCurrentRotation] = useState(0);
  const [isDecelerating, setIsDecelerating] = useState(false);
  const rotationRef = useRef(0);
  const animFrameRef = useRef(null);
  const lastTimeRef = useRef(null);
  const isStoppingRef = useRef(false);

  const cx = 150;
  const cy = 150;
  const outerRadius = 138;
  const innerRadius = 42;
  const anglePerSlice = 360 / numCapacity;

  const partnerLogoUrl = partner?.logoHash
    ? getImageUrlByHash(partner.logoHash, partnerLogo)
    : AutoLogo;

  const isSpinningActive = isSpinningContinuous || isDecelerating;

  // Build Slices SVG Path
  const slices = useMemo(() => {
    return Array.from({ length: numCapacity }, (_, i) => {
      const sliceIndex = i;
      const numVal = i + 1;
      const colorScheme = SLICE_COLORS[i % SLICE_COLORS.length];

      // Segment angles in degrees
      // 0 deg is at Top (12 o'clock)
      const startAngleDeg = sliceIndex * anglePerSlice - 90;
      const endAngleDeg = (sliceIndex + 1) * anglePerSlice - 90;
      const midAngleDeg = startAngleDeg + anglePerSlice / 2;

      // Convert to radians
      const startRad = (startAngleDeg * Math.PI) / 180;
      const endRad = (endAngleDeg * Math.PI) / 180;
      const midRad = (midAngleDeg * Math.PI) / 180;

      // Outer arc points
      const x1 = cx + outerRadius * Math.cos(startRad);
      const y1 = cy + outerRadius * Math.sin(startRad);
      const x2 = cx + outerRadius * Math.cos(endRad);
      const y2 = cy + outerRadius * Math.sin(endRad);

      // Inner arc points
      const x3 = cx + innerRadius * Math.cos(endRad);
      const y3 = cy + innerRadius * Math.sin(endRad);
      const x4 = cx + innerRadius * Math.cos(startRad);
      const y4 = cy + innerRadius * Math.sin(startRad);

      const largeArc = anglePerSlice > 180 ? 1 : 0;

      const pathData = `
        M ${x1} ${y1}
        A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2}
        L ${x3} ${y3}
        A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}
        Z
      `;

      // Text position (around 68% between inner & outer)
      const textRadius = innerRadius + (outerRadius - innerRadius) * 0.62;
      const tx = cx + textRadius * Math.cos(midRad);
      const ty = cy + textRadius * Math.sin(midRad);
      const textRotation = midAngleDeg + 90;

      const isSelected = Number(selectedNumber) === numVal;
      const isWinningSlice = winningNumber !== null && Number(winningNumber) === numVal && !isSpinningActive;

      let fill = colorScheme.fill;
      let textColor = colorScheme.textColor;
      let stroke = "rgba(255,255,255,0.25)";
      let strokeWidth = 1;

      if (isWinningSlice) {
        fill = isWinner ? "#059669" : "#10b981";
        textColor = "#ffffff";
        stroke = "#fef08a";
        strokeWidth = 3;
      } else if (isSelected) {
        fill = "#f59e0b";
        textColor = "#0f172a";
        stroke = "#ffffff";
        strokeWidth = 2.5;
      }

      return {
        number: numVal,
        pathData,
        fill,
        textColor,
        stroke,
        strokeWidth,
        tx,
        ty,
        textRotation,
        isSelected,
        isWinningSlice,
      };
    });
  }, [numCapacity, anglePerSlice, outerRadius, innerRadius, selectedNumber, winningNumber, isWinner, isSpinningActive]);

  // Decorative LED Studs around the rim
  const ledStuds = useMemo(() => {
    const count = 24;
    return Array.from({ length: count }, (_, i) => {
      const angle = ((i * (360 / count) - 90) * Math.PI) / 180;
      const r = 144;
      return {
        x: cx + r * Math.cos(angle),
        y: cy + r * Math.sin(angle),
      };
    });
  }, [cx, cy]);

  // Continuous spin loop (as soon as transaction is approved)
  useEffect(() => {
    if (isSpinningContinuous && !isStoppingRef.current) {
      lastTimeRef.current = performance.now();
      const spinSpeed = 720; // 720 deg/second = 2 full rotations per second

      const spinStep = (now) => {
        if (!lastTimeRef.current) lastTimeRef.current = now;
        const delta = Math.min((now - lastTimeRef.current) / 1000, 0.1);
        lastTimeRef.current = now;

        rotationRef.current = rotationRef.current + spinSpeed * delta;
        setCurrentRotation(rotationRef.current);

        animFrameRef.current = requestAnimationFrame(spinStep);
      };

      animFrameRef.current = requestAnimationFrame(spinStep);

      return () => {
        if (animFrameRef.current) {
          cancelAnimationFrame(animFrameRef.current);
          animFrameRef.current = null;
        }
      };
    } else if (!isSpinningContinuous && !mustSpin && !isDecelerating) {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }
    }
  }, [isSpinningContinuous, mustSpin, isDecelerating]);

  // Deceleration and landing on target number (when VRF result is ready)
  useEffect(() => {
    if (mustSpin && !isStoppingRef.current) {
      isStoppingRef.current = true;
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = null;
      }

      const target = Math.max(1, Math.min(numCapacity, Number(effectiveTarget) || 1));
      const targetIndex = target - 1;

      // To land target slice at 12 o'clock (0 deg):
      // The slice's center is at (targetIndex + 0.5) * anglePerSlice
      const targetSliceCenter = (targetIndex + 0.5) * anglePerSlice;
      const currentMod = rotationRef.current % 360;
      const extraSpins = 4 * 360; // 4 full decelerating rotations
      const finalAngle = rotationRef.current + extraSpins + (360 - currentMod) - targetSliceCenter;

      console.log(`[ModernSpinWheel] Decelerating to target slice #${target}:`, {
        targetSlice: target,
        capacity: numCapacity,
        targetSliceCenterDeg: targetSliceCenter,
        finalRotationDeg: finalAngle,
      });

      rotationRef.current = finalAngle;
      setIsDecelerating(true);
      setCurrentRotation(finalAngle);

      const timer = setTimeout(() => {
        console.log(`[ModernSpinWheel] Landed on slice #${target}. Invoking onStopSpinning callback.`);
        isStoppingRef.current = false;
        setIsDecelerating(false);
        if (onStopSpinning) {
          onStopSpinning();
        }
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [mustSpin, effectiveTarget, numCapacity, anglePerSlice, onStopSpinning]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, []);

  return (
    <Box sx={{ position: "relative", width: 280, height: 280, mx: "auto", my: 1 }}>
      {/* Top Pointer Indicator */}
      <div
        className={`spin-needle-indicator ${isSpinningActive ? "ticking" : ""}`}
        style={{
          position: "absolute",
          top: -4,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 30,
        }}
      />

      {/* SVG Roulette Wheel */}
      <svg
        viewBox="0 0 300 300"
        width="100%"
        height="100%"
        style={{
          filter: "drop-shadow(0 8px 24px rgba(0, 0, 0, 0.35))",
          overflow: "visible",
        }}
      >
        <defs>
          {/* Metallic Gold Outer Bezel Gradient */}
          <linearGradient id="goldBezel" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fef08a" />
            <stop offset="25%" stopColor="#eab308" />
            <stop offset="50%" stopColor="#ca8a04" />
            <stop offset="75%" stopColor="#eab308" />
            <stop offset="100%" stopColor="#713f12" />
          </linearGradient>

          {/* Center Hub Metallic Ring */}
          <radialGradient id="centerHubGrad" cx="40%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#334155" />
            <stop offset="60%" stopColor="#0f172a" />
            <stop offset="100%" stopColor="#020617" />
          </radialGradient>

          {/* Shadow Filter */}
          <filter id="hubShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="3" stdDeviation="4" floodOpacity="0.45" />
          </filter>
        </defs>

        {/* Outer Rim Ring */}
        <circle cx={cx} cy={cy} r={148} fill="url(#goldBezel)" stroke="#0f172a" strokeWidth={2} />
        <circle cx={cx} cy={cy} r={140} fill="#0f172a" />

        {/* LED Studs */}
        {ledStuds.map((stud, idx) => (
          <circle
            key={idx}
            cx={stud.x}
            cy={stud.y}
            r={2.5}
            fill={idx % 2 === 0 ? "#fef08a" : "#ffffff"}
            opacity={isSpinningActive ? 0.9 : 0.75}
          />
        ))}

        {/* Rotating Wheel Group */}
        <g
          style={{
            transformOrigin: `${cx}px ${cy}px`,
            transform: `rotate(${currentRotation}deg)`,
            transition: isDecelerating
              ? "transform 4.0s cubic-bezier(0.12, 0.8, 0.2, 1)"
              : "none",
          }}
        >
          {/* Slices */}
          {slices.map((slice) => (
            <path
              key={slice.number}
              d={slice.pathData}
              fill={slice.fill}
              stroke={slice.stroke}
              strokeWidth={slice.strokeWidth}
            />
          ))}

          {/* Numbers */}
          {slices.map((slice) => (
            <text
              key={`txt-${slice.number}`}
              x={slice.tx}
              y={slice.ty}
              fill={slice.textColor}
              fontSize={numCapacity > 16 ? "11px" : numCapacity > 12 ? "13px" : "15px"}
              fontWeight="800"
              fontFamily="sans-serif"
              textAnchor="middle"
              dominantBaseline="central"
              transform={`rotate(${slice.textRotation}, ${slice.tx}, ${slice.ty})`}
              style={{
                userSelect: "none",
                pointerEvents: "none",
                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))",
              }}
            >
              {slice.number}
            </text>
          ))}
        </g>

        {/* Static Center Hub (Does not spin, always upright) */}
        <circle
          cx={cx}
          cy={cy}
          r={innerRadius}
          fill="url(#centerHubGrad)"
          stroke="url(#goldBezel)"
          strokeWidth={3.5}
          filter="url(#hubShadow)"
        />

        {/* Central Logo/Icon */}
        <image
          href={partnerLogoUrl || AutoLogo}
          x={cx - 18}
          y={cy - 18}
          width={36}
          height={36}
          preserveAspectRatio="xMidYMid meet"
          style={{ pointerEvents: "none" }}
        />
      </svg>
    </Box>
  );
};

export default ModernSpinWheel;
